import type { ReactNode } from "react";

import { screenById } from "../navigation.ts";
import { HeroDisc } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";

/**
 * What a designed screen that is not built says for itself.
 *
 * Five of the eighteen driver screens are not drawn by this build, and none of them is held back by
 * a missing field or by time: **nothing published answers their subject at all.** There is no
 * onboarding-application read for `Onboarding board`, no stop-plan read for `Multi-stop job`, no
 * per-courier earnings read for `9 Earnings`, no handoff reconciliation outside `/internal/v1` for
 * `Handoff`, and no public job read for `Document upload` — whose other absence, the upload route
 * itself, ADR 0025 closed while FIL-03's job read stayed internal.
 *
 * It was eight. `Vehicle`, `4 To pickup` and `5 Scan` left this list on 8 October, and none of them
 * left it because a route landed: two decisions did it. ADR 0034 made a courier's vehicle an
 * assignment record rather than the `vehicle.driverId` column this file used to say it was waiting
 * for, and ADR 0035 made an address a record GEO-02 owns rather than the `pickupAddressLabel` it
 * used to name. Both of those field names are ones the platform deliberately does not publish, so
 * both `waiting` entries would have stayed true-looking for ever.
 *
 * A screen built against a route that does not exist is worse than no screen, because it looks
 * finished. So each of the eight is reachable by its address and says what it is waiting on — which is
 * the one useful thing an application can say about a screen it cannot draw, and is considerably more
 * useful than a blank. The list is in `navigation.ts` beside the operation each built screen reads,
 * and `tests/navigation.test.ts` checks it against
 * `tests/contract/mobile-screen-support.test.ts`'s own measurement so the two cannot drift.
 */
export function ScreenOutlook({ pageId }: { readonly pageId: string }): ReactNode {
  const screen = screenById(pageId);

  if (screen === undefined) {
    return (
      <PhoneShell
        screen="readiness screen--state"
        art={<HeroDisc glyph="help" caption="No such screen" />}
        online={false}
        earningsLabel={null}
        chip={{ class: "closed", label: "no such screen" }}
        title="There's no screen here"
        sub="That address doesn't name a screen of this app."
        actions={[]}
        flag="wording not in the design"
      />
    );
  }

  return (
    <PhoneShell
      screen="readiness screen--state"
      art={<HeroDisc glyph="construction" caption="Not built" />}
      online={false}
      earningsLabel={null}
      chip={{ class: "waiting", label: "not built" }}
      title={screen.title}
      sub={`Designed in "${screen.design}". It isn't built because nothing published answers what it draws.`}
      rows={screen.waiting.map((waiting) => ({
        key: waiting.element,
        value: null,
        because: `needs ${waiting.needs}`,
      }))}
      actions={[]}
      flag="wording not in the design"
    />
  );
}
